import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  School as SchoolIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';

function Home() {
  const { isAuthenticated } = useAuth();

  // Hero images array
  const heroImages = [
    {
      url: 'https://mvypassetse3adbd.blob.core.windows.net/assets/home/home-1.jpg',
      alt: 'Girls playing a game',
    },
    {
      url: 'https://mvypassetse3adbd.blob.core.windows.net/assets/home/home-2.jpg',
      alt: 'Girls playing',
    },
    {
      url: 'https://mvypassetse3adbd.blob.core.windows.net/assets/home/home-3.jpg',
      alt: 'Girls sitting around a tower of plastic cups and smiling',
    },
    {
      url: 'https://mvypassetse3adbd.blob.core.windows.net/assets/home/home-4.jpg',
      alt: 'Boys gathered in a circle',
    },
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-cycle through images every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <>
      {/* Hero Section - background extends behind navbar */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #2d5a42 0%, #1e4034 100%)',
          position: 'relative',
          overflow: 'hidden',
          mt: '-64px', // Negative margin to pull up behind navbar
          pt: '128px', // Padding-top for navbar height + pulled margin
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(124, 179, 66, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(212, 225, 87, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4}>
            <Grid size={12}>
              <Typography
                variant="h1"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  mb: 2,
                  textShadow: '0 2px 12px rgba(0, 0, 0, 0.2)',
                }}
              >
                Welcome to MVYP
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#7cb342',
                  fontWeight: 500,
                  mb: 3,
                }}
              >
                Merrimack Valley Youth Program
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '1.1rem',
                  mb: 4,
                  lineHeight: 1.8,
                }}
              >
                Empowering young Muslims in the Merrimack Valley through faith-based education,
                community engagement, and leadership development. We nurture a confident generation
                grounded in Islamic values and committed to serving society.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  component={RouterLink}
                  to="/programs"
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<SchoolIcon />}
                  sx={{
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                  }}
                >
                  Browse Programs
                </Button>
                {!isAuthenticated && (
                  <Button
                    component={RouterLink}
                    to="/create-account"
                    variant="outlined"
                    size="large"
                    startIcon={<PersonAddIcon />}
                    sx={{
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      color: 'white',
                      borderColor: 'white',
                      '&:hover': {
                        borderColor: '#7cb342',
                        bgcolor: 'rgba(124, 179, 66, 0.1)',
                      },
                    }}
                  >
                    Get Started
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid size={12} sx={{ pb: 4 }}>
              {/* Hero Banner Images */}
              <Grid container spacing={2}>
                {/* Left image - current image */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ position: 'relative', width: '100%', height: '400px' }}>
                    {heroImages.map((image, index) => (
                      <Box
                        key={index}
                        component="img"
                        src={image.url}
                        alt={image.alt}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                          opacity: currentImageIndex === index ? 1 : 0,
                          transition: 'opacity 2s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
                {/* Right image - next image (only on desktop) */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Box sx={{ position: 'relative', width: '100%', height: '400px' }}>
                    {heroImages.map((image, index) => {
                      const nextIndex = (currentImageIndex + 1) % heroImages.length;
                      return (
                        <Box
                          key={index}
                          component="img"
                          src={image.url}
                          alt={image.alt}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                            opacity: nextIndex === index ? 1 : 0,
                            transition: 'opacity 2s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 2,
            }}
          >
            What We Offer
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              mb: 6,
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Our weekly youth program serves middle and high school students with engaging
            activities, mentorship, and Islamic education. Through games, life lessons, and
            meaningful connections, we create a supportive community where young Muslims can
            grow, learn, and thrive together.
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                icon: <GroupsIcon sx={{ fontSize: 50 }} />,
                title: 'Team Building',
                description: 'Connect with peers in a welcoming environment through team-building games, group activities, and special outings like bowling, laser tag, and ski retreats that foster lasting friendships.',
              },
              {
                icon: <SchoolIcon sx={{ fontSize: 50 }} />,
                title: 'Islamic Education',
                description: 'Learn about Islamic figures, history, and values through engaging lessons that help students develop a strong foundation in their faith while building confidence in their identity.',
              },
              {
                icon: <TrophyIcon sx={{ fontSize: 50 }} />,
                title: 'Mentorship & Growth',
                description: 'Benefit from our unique mentor-mentee model where college students and young professionals guide middle and high schoolers, providing positive role models and personalized support for personal development.',
              },
            ].map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default Home;
